import { useMemo } from 'react';

import { useAssistant } from '@core/assistant/providers/Provider';
import { useMytaverseEvent } from '@core/dashboard/providers';

import { aggregateEntities } from './aggregate';
import { useEditorAiData } from './EditorAiDataContext';
import { useRegionIndex } from './useRegionIndex';
import type { AiEntity, AiEntityCategory } from './types';

interface UseAiInspectorEntityParams {
  trigger: string;
  scriptId: string | null;
}

export interface UseAiInspectorEntityResult {
  entity: AiEntity | null;
  ragRefetch: () => Promise<void>;
  ragLoading: boolean;
  mapName: string | undefined;
  brandingId: string | undefined;
  isLiveBranding: boolean;
  // Category to use when synthesising an entity for a trigger/spot that has no
  // script yet (region-canonical): a region — or a spot inside one — creates an
  // AI trigger; a standalone spot creates a spot.
  createCategory: AiEntityCategory;
  // Canonical primary trigger for creation (the owning region when known).
  createPrimary: string;
  // Full trigger set persisted on create: [regionName, ...spots] for a region,
  // [spotId] for a standalone spot.
  createTriggers: string[];
}

/**
 * Resolves a single AiEntity (script + matching RAG items) for the inspector
 * based on the current selection. Reads scripts + RAG from the shared
 * EditorAiData context, then runs the same aggregator that powers the tree so
 * identity stays consistent across panels and a mutation in one view refetches
 * the other immediately.
 */
export const useAiInspectorEntity = ({
  trigger,
  scriptId,
}: UseAiInspectorEntityParams): UseAiInspectorEntityResult => {
  const { currentBrandingId, currentRoom } = useMytaverseEvent();
  const assistant = useAssistant();
  const {
    scripts,
    ragItems,
    ragLoading,
    refetchAll,
  } = useEditorAiData();
  // Everything in the editor is scoped to the branding selected in the
  // top-left dropdown (currentBrandingId) — NOT the assistant's runtime/live
  // branding. Scripts and knowledge from other brandings must never surface
  // here, otherwise a spot/trigger with no script in this branding would show
  // a stale script that belongs to a different branding.
  const brandingId = currentBrandingId ?? undefined;
  const mapName =
    currentRoom?.map?.name ?? assistant?.currentMapName ?? undefined;
  const roomActiveBranding = currentRoom?.activeBrandingId;

  // Scope by the current branding AND map, matching the tree's aggregation, so
  // a mutation that refetches the full list re-resolves this entity against the
  // current brandingId + mapName.
  const entities = useMemo(
    () =>
      aggregateEntities({
        scripts,
        ragItems,
        mapName,
        brandingId,
      }),
    [scripts, ragItems, mapName, brandingId],
  );

  const entity = useMemo<AiEntity | null>(() => {
    if (scriptId) {
      const byScript = entities.find((e) => e.script?.scriptId === scriptId);
      if (byScript) {
        return byScript;
      }
    }
    if (trigger) {
      const byTrigger = entities.find(
        (e) => e.primaryTrigger === trigger || e.aliases.includes(trigger),
      );
      if (byTrigger) {
        return byTrigger;
      }
    }
    return null;
  }, [entities, scriptId, trigger]);

  const isLiveBranding =
    !!brandingId &&
    !!roomActiveBranding &&
    brandingId === roomActiveBranding;

  // Region-canonical create resolution. The selected trigger may be a region
  // name, a spot owned by a region, or a standalone spot. Owned spots resolve
  // to their region so a single script covers the trigger and its spots.
  const regionIndex = useRegionIndex(mapName);
  const { createCategory, createPrimary, createTriggers } = useMemo(() => {
    const lower = trigger.toLowerCase();
    const regionName = regionIndex.regionNames.find(
      (name) => name.toLowerCase() === lower,
    );
    const owningRegion = regionName ?? regionIndex.spotToRegion[lower];
    if (owningRegion) {
      const spots = regionIndex.regionSpots[owningRegion] ?? [];
      return {
        createCategory: 'ai_trigger' as AiEntityCategory,
        createPrimary: owningRegion,
        createTriggers: [owningRegion, ...spots],
      };
    }
    return {
      createCategory: 'spot' as AiEntityCategory,
      createPrimary: trigger,
      createTriggers: trigger ? [trigger] : [],
    };
  }, [trigger, regionIndex]);

  return {
    entity,
    // `ragRefetch` is the public API consumers call after mutating; route it
    // through the shared `refetchAll` so the tree's view refreshes too.
    ragRefetch: refetchAll,
    ragLoading,
    mapName,
    // New scripts/knowledge are created under the current branding.
    brandingId,
    isLiveBranding,
    createCategory,
    createPrimary,
    createTriggers,
  };
};
